import React, { type Key, type ReactNode } from 'react';
import ChevronDown from '@uber/base-icons/chevron-down';
import ChevronRight from '@uber/base-icons/chevron-right';
import { useStyletron } from 'baseui';
import { Panel, StatelessAccordion } from 'baseui/accordion';
type AnnotationSectionEntryT = {
  id: Key;
  header: ReactNode;
  items: ReactNode[];
  isSelected?: boolean;
  containerRef?: React.Ref<HTMLDivElement>;
  containerBackgroundColor?: string;
  headerBackgroundColor?: string;
  contentBackgroundColor?: string;
};
type PropsT = {
  sections: AnnotationSectionEntryT[];
  expandedKeys: Key[];
  onChange: (expandedKeys: Key[]) => void;
  headerBackgroundColor?: string;
};
const PanelToggleIcon = ({
  $expanded
}: {
  $expanded?: boolean;
}) => {
  const [css, theme] = useStyletron();
  return <span className={css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: theme.sizing.scale500,
    marginRight: theme.sizing.scale550,
    transitionProperty: 'transform',
    transitionDuration: theme.animation.timing200,
    transitionTimingFunction: theme.animation.easeOutCurve,
    color: theme.colors.contentSecondary
  })}>
      {$expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
    </span>;
};
const ACCORDION_OVERRIDES = ({
  Root: {
    style: {
      margin: 0
    }
  }
} as const);
const getPanelOverrides = (headerBackgroundColor?: string, contentBackgroundColor?: string, containerBackgroundColor?: string, containerRef?: React.Ref<HTMLDivElement>) => ({
  PanelContainer: {
    props: containerRef ? {
      ref: containerRef
    } : undefined,
    style: ({
      $theme
    }) => {
      return {
        backgroundColor: containerBackgroundColor ?? 'transparent',
        borderBottom: 'none',
        borderRadius: $theme.borders.radius300,
        marginBottom: $theme.sizing.scale300,
        marginTop: $theme.sizing.scale300,
        overflow: 'hidden',
        transitionProperty: 'background-color',
        transitionDuration: $theme.animation.timing200
      };
    }
  },
  Header: {
    style: ({
      $theme
    }) => ({
      display: 'flex',
      alignItems: 'stretch',
      flexDirection: 'row-reverse',
      // to keep the icon on the left
      backgroundColor: headerBackgroundColor ?? 'transparent',
      borderRadius: $theme.borders.radius300,
      padding: `0 ${$theme.sizing.scale200} 0 0`,
      height: $theme.sizing.scale1000,
      transitionProperty: 'background-color',
      transitionDuration: $theme.animation.timing200
    })
  },
  Content: {
    style: ({
      $theme
    }) => ({
      padding: 0,
      backgroundColor: contentBackgroundColor ?? 'transparent',
      borderBottomLeftRadius: $theme.borders.radius300,
      borderBottomRightRadius: $theme.borders.radius300,
      transitionProperty: 'background-color',
      transitionDuration: $theme.animation.timing200
    })
  },
  ToggleIcon: {
    component: PanelToggleIcon
  }
});
export const AnnotationAccordion = ({
  sections,
  expandedKeys,
  onChange,
  headerBackgroundColor
}: PropsT) => {
  return <StatelessAccordion accordion={false} expanded={expandedKeys} overrides={ACCORDION_OVERRIDES} onChange={({
    expanded
  }) => onChange(expanded)}>
      {sections.map(section => <Panel key={section.id} overrides={getPanelOverrides(section.headerBackgroundColor ?? headerBackgroundColor, section.contentBackgroundColor, section.containerBackgroundColor, section.containerRef)} title={section.header}>
          <div>{section.items}</div>
        </Panel>)}
    </StatelessAccordion>;
};